import cn from 'clsx'
import Image from 'next/image'
import s from './LoreTwoProductView.module.scss'
import { FC, useEffect, useState } from 'react'
import type { Product } from '@commerce/types/product'
import usePrice from '@framework/product/use-price'
import {
  ProductSlider,
  LoreTwoProductSidebar,
  LoreTwoProductAddToCart,
} from '@components/product'
import { Container, Text, Grid, CustomImage } from '@components/ui'
import { useUI } from '@components/ui/context'
import { isMobileWidth } from '@utils/helpers'
import { useWindowSize } from '@hooks/useWindowSize'

import { SEO, FAQ } from '@components/common'

import ProductTechnicalSection from '../ProductTechnicalSection'
import ProductShippingSection from '../ProductShippingSection'
import ProductQuestionsSection from '../ProductQuestionsSection'
import {
  HomepageFaqblock as FAQBlockProps,
  LoreTwo as LoreTwoProductPage,
  LoreTwoPageHowSection,
  FindFitterBlock,
  Fitter,
} from '@generated/model/models'
import { FittersBlock, How } from '@components/loretwopage'
import { useRouter } from 'next/router'
import { BREAKPOINT_LG, BREAKPOINT_MD, BREAKPOINT_SM } from 'siteConfig'
import { useJsApiLoader } from '@react-google-maps/api'

interface LoreTwoProductViewProps {
  product: Product
  relatedProducts: Product[]
  page: LoreTwoProductPage
  howSection: LoreTwoPageHowSection
  fittersSection: FindFitterBlock
  fitters: Fitter[]
  faqblock: FAQBlockProps
}

const LoreTwoProductView: FC<LoreTwoProductViewProps> = ({
  product,
  relatedProducts,
  page,
  howSection,
  fittersSection,
  fitters,
  faqblock,
}) => {
  const router = useRouter()
  const slug = router.query.slug

  const defaultOptions: { [key: string]: string } =
    slug && slug.length == product.options.length
      ? product.options
          .filter(
            (opt) => opt.values && opt.values.length > 0 && opt.values[0].label
          )
          .reduce(
            (a, opt, ind) => ({
              ...a,
              [opt.displayName.toLowerCase()]: slug[ind],
            }),
            {}
          )
      : product.options
          .filter(
            (opt) => opt.values && opt.values.length > 0 && opt.values[0].label
          )
          .reduce(
            (a, opt) => ({
              ...a,
              [opt.displayName.toLowerCase()]:
                opt.values[0].label.toLowerCase(),
            }),
            {}
          )

  const defaulVarientNum = product.variants
    .filter((v) => v.defaultImage)
    .findIndex((v) =>
      v.options.every(
        (opt, ind) =>
          opt.values[0].label.toLowerCase() ===
          defaultOptions[opt.displayName.toLowerCase()]
      )
    )

  const defaultSlideNum =
    defaulVarientNum > 0 ? defaulVarientNum + product.images.length : 0

  const [currentSlide, setCurrentSlide] = useState(defaultSlideNum || 0)

  const [currentFitter, setCurrentFitter] = useState<Fitter | null>(null)

  const { price } = usePrice({
    amount: product.price.value,
    baseAmount: product.price.retailPrice,
    currencyCode: product.price.currencyCode!,
  })

  const { setPageTheme, setContainerFixedWidth } = useUI()

  useEffect(() => {
    setPageTheme('LIGHT')
    setContainerFixedWidth()
  }, [])

  const windowSize = useWindowSize()
  const isMobile = isMobileWidth(windowSize.width)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalProps, setModalProps] = useState({
    title: '',
    section: {},
    variant: 'one',
    showImage: false,
  })
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
    libraries: ['places'],
  })

  return (
    <>
      <Container className={s.container} clean>
        <div className={cn(s.root, s.inner)}>
          <div className={cn(s.main, s.inner)}>
            <div className={s.imagesBlock}>
              <ProductSlider
                childClassName={s.imagesContainer}
                disabledOnDesktop={false}
                currentSlide={currentSlide}
                showNavigation={false}
              >
                {product.images.map((image, i) => (
                  <div key={image.url} className={s.imageContainer}>
                    <Image
                      className={s.img}
                      src={image.url!}
                      alt={image.alt || 'Product Image'}
                      width={BREAKPOINT_MD}
                      height={BREAKPOINT_MD}
                      priority={i === 0}
                      quality="85"
                      layout="responsive"
                      objectFit="contain"
                    />
                  </div>
                ))}

                {product.variants
                  .filter((variant) => variant.defaultImage?.urlOriginal)
                  .map((variant, i) => (
                    <div key={`variant-${i}`} className={s.imageContainer}>
                      <Image
                        className={s.img}
                        src={variant.defaultImage?.urlOriginal!}
                        alt={variant.defaultImage?.altText || 'Product Image'}
                        width={BREAKPOINT_MD}
                        height={BREAKPOINT_MD}
                        quality="85"
                        layout="responsive"
                        objectFit="contain"
                      />
                    </div>
                  ))}
              </ProductSlider>
            </div>
          </div>

          <LoreTwoProductSidebar
            product={product}
            relatedProducts={relatedProducts}
            page={page}
            className={s.sidebar}
            setCurrentSlide={setCurrentSlide}
            defaultOptions={defaultOptions}
            fitters={fitters}
            fittersData={fittersSection}
            currentFitter={currentFitter}
            setCurrentFitter={setCurrentFitter}
          />

          <LoreTwoProductAddToCart
            product={product}
            page={page}
            className={s.addToCart}
            setModalProps={setModalProps}
            setModalOpen={setModalOpen}
            defaultOptions={defaultOptions}
          />
        </div>

        <ProductTechnicalSection
          product={product}
          technical_section={page.lore_two_technical_section}
          technical_specifications={page.lore_two_technical_specifications}
          className={s.section}
        />

        <How data={howSection} />

        <FittersBlock
          fitters={fitters}
          data={fittersSection}
          showSelectButton={false}
          currentFitter={currentFitter}
          setCurrentFitter={setCurrentFitter}
          isLoaded={isLoaded}
        />
      </Container>
      <ProductShippingSection
        product={product}
        shipping_section={page.lore_two_shipping_section}
        className={s.section}
      />
      <Container className={s.container} clean>
        <ProductQuestionsSection
          product={product}
          questions_section={page.lore_two_questions_section}
          className={s.section}
        />
        <div className={cn(s.fullbleedSection, s.faq)}>
          {faqblock && (
            <FAQ
              faqs={faqblock.faqs}
              title={faqblock.title}
              expandAll={false}
            />
          )}
        </div>
      </Container>
      <SEO
        title={product.name}
        description={product.description}
        openGraph={{
          type: 'website',
          title: product.name,
          description: product.description,
          images: [
            {
              url: product.images[0]?.url!,
              width: '800',
              height: '600',
              alt: product.name,
            },
          ],
        }}
      />
    </>
  )
}

export default LoreTwoProductView
